import { Box, ModalHeader, Typo, useModal } from '@wrisk/ui-components'
import React, { FunctionComponent } from 'react'
import { useNavigate } from 'react-router-dom'

import { with410ErrorHandling } from '../../../../../clients/api'
import { PolicyAdjustmentResponse } from '../../../../../domain'
import { useErrorHandlingAsyncCallback } from '../../../../../hooks/errors'
import { useNavigateCallback } from '../../../../../hooks/navigation'
import { useStripeMtaAdmin, useStripeMtaUser } from '../../../../../hooks/stripeMta'
import {
  TExtends,
  TKey,
  useWriskTranslation,
} from '../../../../../infrastructure/internationalisation'
import { Page, useCurrentPath } from '../../../../../infrastructure/routing'
import { usePrincipal } from '../../../../authentication'
import { ActionBar } from '../../../../organisms/form'
import { usePaymentSchedule } from '../../../paymentScheduleContext'
import { usePolicy } from '../../policyContext'
import { PolicyAdjustmentResultPath } from './PolicyAdjustmentResultPath'

interface AdjustmentActionBarProps {
  adjustment: PolicyAdjustmentResponse
  parent: Page
}

const tKey = TKey('pages.adjustment-action-bar')

export const AdjustmentActionBar: FunctionComponent<AdjustmentActionBarProps> = ({
  adjustment,
  parent,
}) => {
  const { isAdmin } = usePrincipal()

  const path = useCurrentPath()
  const navigate = useNavigate()

  const modal = useModal()
  const { t } = useWriskTranslation()
  const { apiClient } = usePrincipal()
  const { setPolicy, refresh } = usePolicy()
  const { paymentScheduleType } = usePaymentSchedule()

  const { adjustmentId, policyId, amountDue } = adjustment

  const onStripeMtaUser = useStripeMtaUser({
    adjustmentId,
    successPath: [path, PolicyAdjustmentResultPath.RESULT].join('/'),
    cancelPath: parent.url,
  })

  const onStripeMtaAdmin = useStripeMtaAdmin({
    adjustmentId,
    onSuccess: async () => {
      await refresh()
      navigate(PolicyAdjustmentResultPath.RESULT)
    },
  })

  const onConfirm = useErrorHandlingAsyncCallback(() =>
    with410ErrorHandling(
      async () => {
        if (amountDue > 0 && paymentScheduleType === 'SINGLE_PAYMENT') {
          isAdmin ? await onStripeMtaAdmin.execute() : await onStripeMtaUser.execute()
        } else {
          await apiClient.confirmPolicyAdjustment(adjustmentId)
          setPolicy(await apiClient.getPolicy(policyId))
          navigate(PolicyAdjustmentResultPath.RESULT)
        }
      },
      () =>
        modal.show({
          content: (
            <Box>
              <ModalHeader header={t(tKey('error-gone', 'header'))} />
              <Typo>{t(tKey('error-gone', 'content'))}</Typo>
            </Box>
          ),
        }),
    ),
  )

  const onDismiss = useNavigateCallback(parent.url)

  const actionTKey = amountDue > 0 ? TExtends(tKey, 'debit') : tKey

  return (
    <ActionBar
      tKey={actionTKey}
      onConfirm={onConfirm.execute}
      onDismiss={onDismiss}
      loading={onConfirm.loading}
    />
  )
}
